'use client';

import ease from '@/constants/ease';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { FC, useState } from 'react';

export interface TextMediaQuoteProps {
  title: React.ReactNode;
  features: {
    icon: React.ReactNode;
    title: string;
    description: string;
    list?: string[];
    tag?: string;
    image: React.ReactNode;
  }[];
}

const TextMediaQuote: FC<TextMediaQuoteProps> = ({ title, features = [] }) => {
  const [activeFeatureIndex, setFeatureIndex] = useState(0);
  const activeFeature = features[activeFeatureIndex];

  const mobileNavSlider = (
    <div className="flex md:hidden flex-wrap">
      {features.map((feature, index) => (
        <div key={`ai-feature-${index}`} className={`flex w-full flex-wrap cursor-pointer first:border-t border-b border-solid border-gray-200`} onClick={() => setFeatureIndex(index)}>
          <div className={`flex w-full justify-between font-500 text-p1 pt-[24px] ${activeFeatureIndex === index ? "pb-0" : "py-[24px]"}`}>
            <div className="flex">
              {feature.title}
            </div>
            <div className="flex">
              {activeFeatureIndex === index
                ?
                <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 28 28" fill="none">
                  <path d="M9.3335 14H18.6668" stroke="#0D1D39" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                :
                <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 28 28" fill="none">
                  <path d="M14 9.33333V18.6667" stroke="#0D1D39" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M9.3335 14H18.6668" stroke="#0D1D39" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              }
            </div>

          </div>
          {activeFeatureIndex === index && (
            <motion.div
              className={`flex flex-wrap text-p2 pb-[32px] justify-center`}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 400 / 1000,
                ease: ease.appear,
              }}
            >
              {feature.description}
              <div className="flex mt-[16px]">
                {feature.image}
              </div>
            </motion.div>
          )}
        </div>
      ))}
    </div>
  );

  const desktopNav = (
    <div className="hidden md:flex flex-col gap-2">
      {features.map((feature, index) => (
        <div key={`desktop-feature-${feature.title?.replace(/\s/g, '-') ?? index}`} className="keen-slider__slide">
          <div
            className={cx(
              'p-3 flex flex-col gap-2 rounded-20 relative transition-colors duration-200 ease-in-out-cubic',
              {
                'bg-white-100': index === activeFeatureIndex,
                'border-transparent': index !== activeFeatureIndex,
              },
            )}
          >
            <div className="flex flex-col gap-1">
              <div className="flex gap-1 items-center">
                <button
                  onClick={() => setFeatureIndex(index)}
                  className={`block link-cover text-20/28 font-500 tracking-tight ${index === activeFeatureIndex ? "text-gray-900" : "text-blue-900"}`}
                >
                  {feature.title}
                </button>
                {feature.tag && (
                  <span className="bg-citron-500 text-citron-900 block rounded-4 px-half text-10/20 h-[20px] uppercase font-mono font-800">
                    {feature.tag}
                  </span>
                )}
              </div>
            </div>
            {index === activeFeatureIndex && (
              <motion.div
                className="text-p2 text-gray-700"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 400 / 1000,
                  ease: ease.appear,
                }}
              >
                {feature.description}
                {feature.list &&
                  <ul className='list-disc ps-2 pt-2'>
                    {feature.list.map(item =>
                      <li className="marker:text-blue-900 mb-[10px]" key={item}>{item}</li>
                    )}
                  </ul>
                }
              </motion.div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-white px-container-mobile md:px-container-desktop py-6 md:py-20 relative overflow-hidden">
      <div className="mx-auto max-w-grid">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-9 relative z-1">
          <div className="md:col-span-6 lg:col-span-5 flex flex-col gap-6">
            <div className="flex flex-col gap-3 items-start">
              <h2 className="text-h2 text-blue-900 md:text-h1 font-500 tracking-tight">{title}</h2>
            </div>
            {desktopNav}
          </div>
          <div className="md:col-span-6 lg:col-span-6 lg:col-start-7 flex flex-col gap-4">
            <div className="hidden md:flex relative overflow-hidden">
              <AnimatePresence>
                {activeFeature && (
                  <motion.div
                    key={activeFeatureIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                    transition={{
                      duration: 400 / 1000,
                      ease: ease.appear,
                    }}
                  >
                    {activeFeature.image}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {mobileNavSlider}
          </div>
        </div>
        <div className="flex flex-wrap max-w-full justify-center mt-[120px]">
          <div className="flex w-full justify-center">
            <Image src="https://front.com/webassets/rebrand/customer-logo-branch-dark-blue.svg" width={100} height={40} alt="Branch logo" />
          </div>
          <div className="flex max-w-[1024px] font-500 text-h2 text-center font-serif mt-[30px]">
            “After switching from Zendesk, we saw productivity improve by 40% in our member support team. Front allowed us to create a best-in-breed customer experience across email, text, and chat.”
          </div>
          <div className="flex w-full justify-center items-center mt-[24px]">
            <span className="text-p2 text-gray-700 font-500">Joe Emison</span>
            <span className="uppercase text-gray-700 text-caption1 ml-[20px]">cto & co-founder</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextMediaQuote;
